import React, { useRef, useEffect, useState } from "react";

interface RenderCounterProps {
  hookName?: string;
  children?: React.ReactNode;
}

/**
 * Component to test and monitor any React hook for unnecessary rerenders
 *
 * Usage:
 * 1. Wrap your hook call with this component
 * 2. Check the console for render counts
 * 3. Watch for unexpected high render frequencies
 *
 * Expected behavior:
 * - Initial render count should be low (< 10 renders in first few seconds)
 * - Should not continuously rerender when no user interactions occur
 * - State changes should trigger minimal rerenders
 */
export const HookRenderTester: React.FC<RenderCounterProps> = ({ hookName = "Hook", children }) => {
  const renderCount = useRef(0);
  const lastRenderTime = useRef(Date.now());
  const [renderStats, setRenderStats] = useState({ count: 0, avgInterval: 0 });

  // Track renders
  useEffect(() => {
    renderCount.current += 1;
    const currentTime = Date.now();
    const interval = currentTime - lastRenderTime.current;
    lastRenderTime.current = currentTime;

    console.log(`[${hookName}] Render #${renderCount.current}, interval: ${interval}ms`);

    // Update stats every 10 renders
    if (renderCount.current % 10 === 0) {
      setRenderStats({
        count: renderCount.current,
        avgInterval: interval,
      });
    }
  });

  return (
    <>
      {children}
      <div className="fixed top-2 right-2 bg-black/80 text-white p-2 rounded text-xs z-[9999]">
        <div>
          🔄 {hookName} Renders: {renderStats.count}
        </div>
        <div>⏱️ Avg Interval: {renderStats.avgInterval}ms</div>
      </div>
    </>
  );
};

/**
 * Hook to measure rerender frequency of any React hook
 * Returns render statistics for monitoring
 */
export const useRenderMonitor = (componentName: string) => {
  const renderCount = useRef(0);
  const startTime = useRef(Date.now());
  const lastRenderTime = useRef(Date.now());
  const renderTimes = useRef<number[]>([]);
  const recentRenderTimes = useRef<number[]>([]); // Track recent renders

  // Increment render count
  renderCount.current++;

  const now = Date.now();
  const totalTime = now - startTime.current;
  const timeSinceLastRender = now - lastRenderTime.current;

  // Store render timestamp
  renderTimes.current.push(now);
  recentRenderTimes.current.push(now);

  // Keep only renders from last 60 seconds for recent stats
  const oneMinuteAgo = now - 60000;
  recentRenderTimes.current = recentRenderTimes.current.filter((time) => time > oneMinuteAgo);

  lastRenderTime.current = now;

  // Calculate frequencies
  const avgFrequency = totalTime > 0 ? renderCount.current / (totalTime / 1000) : 0;
  const recentFrequency =
    recentRenderTimes.current.length > 1
      ? recentRenderTimes.current.length / 60 // renders per second in last minute
      : 0;

  // Check if component has stabilized (less than 1 render per second in last 10 seconds)
  const tenSecondsAgo = now - 10000;
  const recentTenSecondRenders = recentRenderTimes.current.filter((time) => time > tenSecondsAgo).length;
  const isStable = recentTenSecondRenders < 10;

  // Log to console with emojis
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log(
        `[${componentName}] Renders: ${renderCount.current}, ` +
          `Avg frequency: ${avgFrequency.toFixed(2)}/sec, ` +
          `Recent frequency: ${recentFrequency.toFixed(2)}/sec, ` +
          `Last interval: ${timeSinceLastRender}ms`
      );
    }, 0);

    return () => clearTimeout(timer);
  });

  return {
    renderCount: renderCount.current,
    totalTime,
    avgFrequency,
    recentFrequency,
    lastMinuteRenders: recentRenderTimes.current.length,
    isStable,
  };
};

export default useRenderMonitor;

/**
 * Example usage in your component that uses useCodicentApp:
 *
 * const MyComponent = ({ auth0 }) => {
 *   const renderStats = useRenderMonitor('useCodicentApp');
 *   const codicentApp = useCodicentApp({ auth0 });
 *
 *   return <div>Your component content</div>;
 * };
 */
